import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import storage from '../utils/storage';

/** Paths that must work without signing in (legal, contact, marketing). */
const PUBLIC_PATHS = new Set(['/contact', '/privacy-policy', '/privacy', '/delete-account']);

const MobileRouteGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, loading } = useBookMyOpAuth();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only apply route guard on mobile devices
    // Wait for auth context to finish loading before checking
    // Only check once to prevent multiple redirects
    if (isMobile && !loading && !hasCheckedRef.current) {
      if (PUBLIC_PATHS.has(location.pathname)) {
        hasCheckedRef.current = true;
        return;
      }
      hasCheckedRef.current = true;
      const checkAuth = async () => {
        const isNative = Capacitor.isNativePlatform();
        
        let accessToken = null;
        let refreshToken = null;
        let userData = null;
        
        // On native platforms, ALWAYS check Capacitor Preferences first
        // localStorage is cleared when app is completely closed on Android
        if (isNative) {
          console.log('📱 MobileRouteGuard: Native platform - checking Capacitor Preferences first...');
          try {
            accessToken = await storage.getItem('bookmyop_access_token');
            refreshToken = await storage.getItem('bookmyop_refresh_token');
            userData = await storage.getItem('bookmyop_user_data');
            
            console.log('📱 MobileRouteGuard: Preferences read:', {
              hasAccessToken: !!accessToken,
              hasRefreshToken: !!refreshToken,
              hasUserData: !!userData
            });
            
            // If found in Preferences, also write to localStorage for faster future access
            if (accessToken) {
              try {
                localStorage.setItem('bookmyop_access_token', accessToken);
                console.log('✅ Restored token from Preferences to localStorage');
              } catch (e) {
                console.warn('Could not restore token to localStorage:', e);
              }
            }
            if (refreshToken) {
              try {
                localStorage.setItem('bookmyop_refresh_token', refreshToken);
                console.log('✅ Restored refresh token from Preferences to localStorage');
              } catch (e) {
                console.warn('Could not restore refresh token to localStorage:', e);
              }
            }
            if (userData) {
              try {
                localStorage.setItem('bookmyop_user_data', userData);
                console.log('✅ Restored user data from Preferences to localStorage');
              } catch (e) {
                console.warn('Could not restore user data to localStorage:', e);
              }
            }
          } catch (err) {
            console.error('❌ Error reading from Preferences:', err);
            // Fallback to sync read if Preferences fails
            accessToken = storage.getItemSync('bookmyop_access_token');
            refreshToken = storage.getItemSync('bookmyop_refresh_token');
            userData = storage.getItemSync('bookmyop_user_data');
          }
        } else {
          // On web: Use sync read (localStorage - fast)
          console.log('🌐 MobileRouteGuard: Web platform - using localStorage...');
          accessToken = storage.getItemSync('bookmyop_access_token');
          refreshToken = storage.getItemSync('bookmyop_refresh_token');
          userData = storage.getItemSync('bookmyop_user_data');
        }
        
        // Validate stored user data
        // If we have tokens or user data, validate it before redirecting
        if (userData) {
          try {
            const parsedUserData = typeof userData === 'string' ? JSON.parse(userData) : userData;
            if (parsedUserData && parsedUserData.id) {
              // We have valid user data, allow access even if context hasn't initialized yet
              console.log('✅ MobileRouteGuard: Valid user data found, allowing access');
              return;
            }
          } catch (error) {
            console.warn('⚠️ MobileRouteGuard: Error parsing user data:', error);
          }
        }
        
        // CRITICAL: If we have tokens OR user data, allow access
        // Don't rely on isAuthenticated from context - it might not be initialized yet
        // The auth context will pick up the tokens and initialize properly
        if (accessToken || refreshToken || userData) {
          console.log('✅ MobileRouteGuard: Auth data found in storage, allowing access (auth context will initialize)');
          return; // Allow access - auth context will handle initialization
        }
        
        // Only redirect if we have NO tokens AND NO user data
        // This means user is truly not logged in
        console.log('🚫 MobileRouteGuard: No authentication data found in storage, redirecting to login');
        navigate('/login', { replace: true });
      };
      
      checkAuth();
    }
  }, [isMobile, isAuthenticated, loading, navigate, location.pathname]);

  // On desktop, always render children regardless of auth status
  if (!isMobile) {
    return children;
  }

  // On mobile: Wait for auth to initialize, but don't block based on isAuthenticated
  // The checkAuth function will handle redirecting if truly not authenticated
  // Don't block rendering - let the guard check storage directly
  if (loading && !PUBLIC_PATHS.has(location.pathname)) {
    return null; // Wait for auth context to initialize
  }

  // Always render children on mobile - checkAuth will redirect if needed
  // Don't block based on isAuthenticated because it might not be set yet even if tokens exist
  return children;
};

export default MobileRouteGuard; 